import { ComponentProps } from 'preact/compat';
import { useEffect, useRef } from 'preact/hooks';

import { useIsMobileLayout } from '@/components/LayoutDetector/hooks';
import { cn } from '@/lib/utils';

interface HomeVideoPlayerContainerProps extends Omit<ComponentProps<'div'>, 'ref'> {
  miniMode?: boolean;
  hideOnMiniMode?: boolean;
}

/**
 * It is the container of the video player and other related components like controls or info bar under the video.
 * On mobile mini mode, it is styled with `position: fixed` to make it stay in the bottom of the screen.
 * On desktop mini mode, it should only located inside the main player so it stay in the bottom of player.
 */
const HomeVideoPlayerContainer = ({
  children,
  miniMode,
  className,
  hideOnMiniMode,
  ...rest
}: HomeVideoPlayerContainerProps) => {
  const isMobileLayout = useIsMobileLayout();
  const hasBeenMiniRef = useRef(false);

  useEffect(() => {
    if (miniMode) {
      hasBeenMiniRef.current = true;
    }
  }, [miniMode]);

  return (
    <div
      data-mini-mode={miniMode}
      data-tour="home-video-section"
      className={cn(
        'group/home-player-container flex-col',
        miniMode &&
          (isMobileLayout
            ? 'fixed inset-x-0 bottom-[var(--mobileFooterHeight)] z-10 bg-primary-green mobile:flex-row'
            : 'absolute right-2 top-2 z-10 w-[25%] min-w-[198px] origin-top-right animate-miniPlayerShrink rounded-lg shadow-lg shadow-black/70 transition-[transform,box-shadow] duration-200 ease-out hover:scale-[1.07] hover:shadow-xl hover:shadow-black/50'),
        !miniMode && !isMobileLayout && hasBeenMiniRef.current && 'origin-top-right animate-miniPlayerExpand',
        hideOnMiniMode && miniMode ? 'hidden' : 'flex',
        className,
      )}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
    >
      {children}
    </div>
  );
};

export default HomeVideoPlayerContainer;
